// Shared distribution-listing view for Bandsintown / TicketWeb / Resident Advisor.
// One chronological "timeline scroll" (same as /list): all our events, past above
// and future below, with the first upcoming event parked at the top, a sticky
// header, and a "Jump to today" button. Each upcoming event is marked Listed or
// shows a publish button; past events are muted history.
//
// Backend contract (GET listEndpoint): { connected,
//   summary{listed,notListed,ourTotal, pending?},
//   ourEvents[{ id, date, startAt, venueTz, talent, status, upcoming, listed,
//               pending?, url, ticketmaster? }] }.
// `pending` = pushed to the platform but awaiting its approval queue (RA holds
// new listings ~24h). Rendered amber with NO publish button (a second submit
// would duplicate the listing); platforms that never send it are unaffected.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api.js';
import {
  ticketmasterListingDisplay,
  ticketmasterListingNeedsAttention,
} from '@/lib/ticketmasterListing.js';
import { useTimelineScroll, JumpToTodayButton } from '@/lib/useTimelineScroll.jsx';

function fmtDate(d) {
  if (!d) return '—';
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

function fmtTime(startAt, tz) {
  if (!startAt) return null;
  try {
    return new Date(startAt).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz || 'America/New_York',
    });
  } catch { return null; }
}

function TicketmasterCell({ event, onRequeue, busy, err }) {
  if (!event.upcoming || !event.listed) return <span className="text-muted-foreground/60">—</span>;
  const display = ticketmasterListingDisplay(event.ticketmaster);
  // Re-publish is offered only when TicketWeb knows the shell (a ticketwebId)
  // and Ticketmaster has not accepted it yet. It re-saves the existing shell;
  // it never creates one.
  const canRequeue = !!onRequeue && display.label !== 'Synced' && !!event.ticketwebId;

  return (
    <span className="inline-flex items-center gap-1.5 font-medium">
      <span className={`inline-flex items-center gap-1.5 ${display.label === 'Synced' ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}
        title={event.ticketmaster?.status ? `TicketWeb status: ${event.ticketmaster.status}` : undefined}>
        {display.label}
        {display.verified && (
          <span
            className="text-emerald-600 dark:text-emerald-400"
            aria-label="Verified in Ticketmaster Discovery."
            title="Verified in Ticketmaster Discovery."
          >
            ✓
          </span>
        )}
      </span>
      {canRequeue && (
        <button
          onClick={() => onRequeue(event)}
          disabled={busy}
          title="Re-save the shell on TicketWeb so it re-queues for Ticketmaster publishing"
          className="px-2 py-0.5 rounded-md border border-input text-xs font-normal hover:bg-accent disabled:opacity-50"
        >
          {busy ? 'Re-publishing…' : 'Re-publish'}
        </button>
      )}
      {err && <span className="text-xs font-normal text-red-600" title={err}>{err}</span>}
    </span>
  );
}

export default function DistributionListings({
  title,            // "Resident Advisor"
  platform,         // "resident_advisor" (publish POST target)
  listEndpoint,     // "resident_advisor/listings"
  actionLabel,      // "List on Resident Advisor"
  actioningLabel,   // "Listing…"
  notConnectedMsg,  // shown when {connected:false}
  readOnly = false, // no publish button (platforms managed via announce/sync + per-event editor)
  editHref,         // (ev) => path — adds an Edit link per row to the per-event editor
  showTicketmasterDiagnostics = false,
}) {
  const { tenantSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [show, setShow] = useState('all'); // all | listed | not
  const [busy, setBusy] = useState({});
  const [rowErr, setRowErr] = useState({});
  const [refreshingDiagnostics, setRefreshingDiagnostics] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [requeueBusy, setRequeueBusy] = useState({});   // keyed by ticketwebId
  const [requeueErr, setRequeueErr] = useState({});

  const todayStr = new Date().toLocaleDateString('en-CA'); // local YYYY-MM-DD

  async function publish(ev) {
    setBusy((b) => ({ ...b, [ev.id]: true }));
    setRowErr((r) => ({ ...r, [ev.id]: null }));
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/events/${ev.id}/distribution/${platform}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) {
        const msg = d.error === 'read_only_mode' ? 'Read-only mode is on, publishing is disabled'
          : d.error === 'creds_missing' ? `${title} not connected`
          : d.reason === 'create_save_not_yet_enabled' ? 'Shell create is not enabled yet, pending live verification'
          : d.detail || d.error || d.reason || `Failed (${r.status})`;
        throw new Error(msg);
      }
      // A platform with an approval queue answers pending:true — land the row
      // amber (what actually happened) instead of green.
      const wentPending = !!d.pending;
      setData((cur) => ({
        ...cur,
        ourEvents: cur.ourEvents.map((x) => (x.id === ev.id
          ? { ...x, listed: !wentPending, pending: wentPending, url: d.url || x.url }
          : x)),
        summary: {
          ...cur.summary,
          listed: cur.summary.listed + (wentPending ? 0 : 1),
          pending: (cur.summary.pending || 0) + (wentPending ? 1 : 0),
          notListed: cur.summary.notListed - 1,
        },
      }));
    } catch (e) {
      setRowErr((r) => ({ ...r, [ev.id]: e.message }));
    } finally {
      setBusy((b) => ({ ...b, [ev.id]: false }));
    }
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch(`/api/tenants/${tenantSlug}/${listEndpoint}`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setData(d); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [tenantSlug, listEndpoint]);

  // Re-save an existing TicketWeb shell so it re-queues for Ticketmaster.
  async function requeue(ev) {
    const twId = String(ev.ticketwebId);
    setRequeueBusy((b) => ({ ...b, [twId]: true }));
    setRequeueErr((r) => ({ ...r, [twId]: null }));
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/ticketweb/events/${twId}/requeue`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = d.error === 'read_only_mode' ? 'Read-only mode is on, publishing is disabled'
          : d.error === 'creds_missing' ? `${title} not connected`
          : d.error === 'requeue_in_flight' ? 'Already re-publishing'
          : d.detail || d.error || `Failed (${r.status})`;
        throw new Error(msg);
      }
      const after = d.after ? { ...(ev.ticketmaster || {}), ...d.after, checkedAt: new Date().toISOString() } : ev.ticketmaster;
      const patch = (x) => (String(x.ticketwebId) === twId ? { ...x, ticketmaster: after } : x);
      setData((cur) => ({
        ...cur,
        ourEvents: (cur.ourEvents || []).map(patch),
        ticketwebOnly: (cur.ticketwebOnly || []).map(patch),
      }));
      if (!d.ok) {
        setRequeueErr((r) => ({ ...r, [twId]: `TicketWeb still says: ${d.after?.status || 'not synced'}` }));
      }
    } catch (e) {
      setRequeueErr((r) => ({ ...r, [twId]: e.message }));
    } finally {
      setRequeueBusy((b) => ({ ...b, [twId]: false }));
    }
  }

  async function refreshDiagnostics() {
    setRefreshingDiagnostics(true);
    setRefreshError(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/${listEndpoint}?refresh=1`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) throw new Error(d.detail || d.error || `Refresh failed (${r.status})`);
      setData(d);
    } catch (e) {
      setRefreshError('Ticketmaster refresh failed. Try again.');
    } finally {
      setRefreshingDiagnostics(false);
    }
  }

  const q = filter.trim().toLowerCase();
  const rows = useMemo(() => {
    if (!data?.ourEvents) return [];
    return data.ourEvents.filter((e) => {
      if (show === 'listed' && !(e.upcoming && e.listed)) return false;
      if (show === 'pending' && !(e.upcoming && e.pending)) return false;
      if (show === 'not' && !(e.upcoming && !e.listed && !e.pending)) return false;
      if (show === 'tm_attention' && !(e.upcoming && e.listed && ticketmasterListingNeedsAttention(e.ticketmaster))) return false;
      if (q && !String(e.talent || '').toLowerCase().includes(q) && !String(e.date).includes(q)) return false;
      return true;
    });
  }, [data, show, q]);

  const ticketwebOnlyRows = useMemo(() => {
    if (!showTicketmasterDiagnostics || !data?.ticketwebOnly) return [];
    if (show === 'not' || show === 'pending') return [];
    return data.ticketwebOnly.filter((e) => {
      if (show === 'tm_attention' && !ticketmasterListingNeedsAttention(e.ticketmaster)) return false;
      if (q && !String(e.name || '').toLowerCase().includes(q) && !String(e.date).includes(q)) return false;
      return true;
    });
  }, [data, show, q, showTicketmasterDiagnostics]);

  const firstUpcomingIdx = useMemo(
    () => rows.findIndex((e) => (e.date || '') >= todayStr),
    [rows, todayStr],
  );

  const { listWrapRef, listH, showJump, goHome, onListScroll } = useTimelineScroll({
    ready: !loading && !!data?.connected,
    chronological: true,
    deps: [rows.length],
  });

  if (loading) return <div className="text-sm text-muted-foreground">Loading {title} listings…</div>;
  if (error) return <div className="text-sm text-red-600">Failed to load: {error}</div>;

  if (!data?.connected) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">{title}</h1>
        <p className="text-sm text-muted-foreground">{notConnectedMsg}</p>
      </div>
    );
  }
  const s = data.summary;
  const tmSummary = showTicketmasterDiagnostics ? (s.ticketmaster || {
    synced: 0, discoveryVerified: 0, needsAttention: 0,
  }) : null;
  const tmIssueCount = tmSummary?.needsAttention || 0;

  // Status cell: upcoming → Listed link / Pending badge / publish button;
  // past → muted dash.
  const StatusCell = (e) => {
    if (!e.upcoming) return <span className="text-muted-foreground/60">—</span>;
    if (e.pending) {
      const inner = (
        <>
          <span className="h-2 w-2 rounded-full bg-amber-500" /> Pending approval
        </>
      );
      return e.url ? (
        <a href={e.url} target="_blank" rel="noreferrer"
          title={`Submitted to ${title}; awaiting their approval. No action needed.`}
          className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-medium hover:underline">
          {inner}
        </a>
      ) : (
        <span title={`Submitted to ${title}; awaiting their approval. No action needed.`}
          className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-medium">
          {inner}
        </span>
      );
    }
    if (e.listed) {
      return e.url ? (
        <a href={e.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-medium hover:underline">
          <span className="h-2 w-2 rounded-full bg-emerald-500" /> Listed
        </a>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-medium">
          <span className="h-2 w-2 rounded-full bg-emerald-500" /> Listed
        </span>
      );
    }
    if (readOnly) {
      return (
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <span className="h-2 w-2 rounded-full bg-muted-foreground/40" /> Not listed
        </span>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={() => publish(e)}
          disabled={busy[e.id]}
          className="px-2 py-1 rounded-md border border-input text-xs hover:bg-accent disabled:opacity-50"
        >
          {busy[e.id] ? actioningLabel : actionLabel}
        </button>
        {rowErr[e.id] && <span className="text-xs text-red-600" title={rowErr[e.id]}>{rowErr[e.id]}</span>}
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <div className="flex items-center gap-3">
          <div className="text-sm text-right text-muted-foreground">
            <div>{s.listed} of {(s.upcomingTotal ?? s.ourTotal)} upcoming listed{s.pending > 0 ? `, ${s.pending} pending approval` : ''}</div>
            {tmSummary && (
              <div>
                {tmSummary.synced} synced
                {` · ${tmSummary.discoveryVerified} Discovery-verified`}
                {` · ${tmSummary.needsAttention} need attention`}
              </div>
            )}
          </div>
          {showTicketmasterDiagnostics && (
            <button
              type="button"
              onClick={refreshDiagnostics}
              disabled={refreshingDiagnostics}
              className="px-3 py-1.5 rounded-md border border-input text-sm hover:bg-accent disabled:opacity-50"
            >
              {refreshingDiagnostics ? 'Refreshing…' : 'Refresh Ticketmaster'}
            </button>
          )}
        </div>
      </div>

      {refreshError && <div className="mb-3 text-sm text-red-600">{refreshError}</div>}

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          type="text"
          placeholder="Filter by name or date…"
          className="flex-1 min-w-[180px] px-3 py-1.5 rounded-md border border-input bg-background text-sm"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        {[
          ['all', `All (${s.ourTotal})`],
          ['listed', `Listed (${s.listed})`],
          ...(s.pending > 0 ? [['pending', `Pending (${s.pending})`]] : []),
          ['not', `Not listed (${s.notListed})`],
          ...(showTicketmasterDiagnostics ? [['tm_attention', `TM needs attention (${tmIssueCount})`]] : []),
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setShow(key)}
            className={`px-3 py-1.5 rounded-md border text-sm ${show === key ? 'bg-accent text-accent-foreground border-accent' : 'border-input text-muted-foreground hover:bg-accent/50'}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div ref={listWrapRef} className="relative">
        {/* Mobile: timeline-scroll of cards */}
        <div data-timeline-scroll onScroll={onListScroll} style={{ height: listH ? `${listH}px` : '70vh' }} className="md:hidden overflow-y-auto border border-border rounded-md relative">
          {rows.map((e, idx) => (
            <div
              key={e.id}
              data-first-upcoming={idx === firstUpcomingIdx || undefined}
              className={`p-3 text-sm ${idx === firstUpcomingIdx ? 'border-t-2 border-primary/50' : 'border-t border-border'} ${idx % 2 === 1 ? 'bg-muted/30' : ''} ${!e.upcoming ? 'opacity-50' : ''}`}
            >
              <div className="font-medium">{e.talent || '—'}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {fmtDate(e.date)}
                {fmtTime(e.startAt, e.venueTz) && <span> · {fmtTime(e.startAt, e.venueTz)}</span>}
              </div>
              <div className="mt-2 flex items-center gap-3">
                {StatusCell(e)}
                {editHref && (
                  <Link to={editHref(e)} className="text-xs text-muted-foreground underline hover:text-foreground">Edit</Link>
                )}
              </div>
              {showTicketmasterDiagnostics && e.upcoming && e.listed && (
                <div className="mt-3 border-t border-border pt-2">
                  <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Ticketmaster</div>
                  <TicketmasterCell event={e} onRequeue={requeue} busy={!!requeueBusy[String(e.ticketwebId)]} err={requeueErr[String(e.ticketwebId)]} />
                </div>
              )}
            </div>
          ))}
          {!rows.length && <div className="text-center text-muted-foreground py-6 text-sm">No events match.</div>}
        </div>

        {/* Desktop: timeline-scroll table */}
        <div data-timeline-scroll onScroll={onListScroll} style={{ height: listH ? `${listH}px` : '70vh' }} className="hidden md:block overflow-auto border border-border rounded-md relative">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 font-medium w-56">Date</th>
                <th className="px-3 py-2 font-medium">Event</th>
                <th className="px-3 py-2 font-medium w-48">{title}</th>
                {showTicketmasterDiagnostics && <th className="px-3 py-2 font-medium w-40">Ticketmaster</th>}
                {editHref && <th className="px-3 py-2 font-medium w-16" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((e, idx) => (
                <tr
                  key={e.id}
                  data-first-upcoming={idx === firstUpcomingIdx || undefined}
                  className={`${idx === firstUpcomingIdx ? 'border-t-2 border-primary/50' : 'border-t border-border'} ${idx % 2 === 1 ? 'bg-muted/30' : ''} ${!e.upcoming ? 'opacity-50' : ''}`}
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    {fmtDate(e.date)}
                    {fmtTime(e.startAt, e.venueTz) && <span className="text-muted-foreground"> · {fmtTime(e.startAt, e.venueTz)}</span>}
                  </td>
                  <td className="px-3 py-2">{e.talent || '—'}</td>
                  <td className="px-3 py-2">{StatusCell(e)}</td>
                  {showTicketmasterDiagnostics && <td className="px-3 py-2"><TicketmasterCell event={e} onRequeue={requeue} busy={!!requeueBusy[String(e.ticketwebId)]} err={requeueErr[String(e.ticketwebId)]} /></td>}
                  {editHref && (
                    <td className="px-3 py-2">
                      <Link to={editHref(e)} className="text-xs text-muted-foreground underline hover:text-foreground">Edit</Link>
                    </td>
                  )}
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={3 + (showTicketmasterDiagnostics ? 1 : 0) + (editHref ? 1 : 0)} className="px-3 py-3 text-muted-foreground">No events match.</td></tr>}
            </tbody>
          </table>
        </div>

        <JumpToTodayButton show={showJump} onClick={() => goHome(true)} />
      </div>

      {showTicketmasterDiagnostics && ticketwebOnlyRows.length > 0 && (
        <section className="mt-6">
          <div className="mb-2">
            <h2 className="text-base font-semibold">TicketWeb-only events</h2>
            <p className="text-xs text-muted-foreground">
              Present in TicketWeb but not linked to this calendar. Review duplicates before creating or relinking anything.
            </p>
          </div>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium w-56">Date</th>
                  <th className="px-3 py-2 font-medium">Event</th>
                  <th className="px-3 py-2 font-medium w-48">TicketWeb</th>
                  <th className="px-3 py-2 font-medium w-40">Ticketmaster</th>
                </tr>
              </thead>
              <tbody>
                {ticketwebOnlyRows.map((e, idx) => (
                  <tr key={e.id} className={`border-t border-border ${idx % 2 === 1 ? 'bg-muted/30' : ''}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.date)}</td>
                    <td className="px-3 py-2">{e.name || '—'}</td>
                    <td className="px-3 py-2">{StatusCell(e)}</td>
                    <td className="px-3 py-2"><TicketmasterCell event={e} onRequeue={requeue} busy={!!requeueBusy[String(e.ticketwebId)]} err={requeueErr[String(e.ticketwebId)]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
